import StatusTag from '@components/status-tag';
import BackgroundLetterAvatars from '@components/ui/background-letter-avatars';
import { WES_ORG } from '@global/constants/general';
import { TxnState } from '@global/constants/verification';
import { isClientCareerMosaic } from '@global/helpers/general';
import ChevronRightIcon from '@mui/icons-material/ArrowForwardIos';
import { Avatar, Box, Stack, SxProps, Typography } from '@mui/material';
import { ReactNode, useState } from 'react';
import styles from './styles';

interface VerificationTileProps {
  onClick: VoidFunction;
  iconImgUrl?: string;
  iconComp?: ReactNode;
  iconWrapperColor?: string;
  iconWrapperHeight?: string;
  label: string;
  status?: TxnState | undefined;
  disabled?: boolean;
  marksheetRequired?: boolean;
  noShadow?: boolean;
  labelSx?: SxProps;
  subtitle?: string;
  sx?: SxProps;
  mrp?: string;
  discountedPrice?: string;
  discountInfo?: string;
  rightAdorment?: ReactNode;
}

/**
 * @see {@link iconComp} - Use the following props only with this prop ('iconComp'). This can be used when there's no url for an icon.
 * @see {@link iconWrapperColor}
 * @see {@link iconWrapperHeight}
 *
 * @see {@link iconImgUrl} - Use the following props only with this prop ('iconImgUrl'). This can be used when there's an icon url.
 */
function VerificationTile(props: VerificationTileProps) {
  const {
    onClick,
    iconImgUrl,
    iconComp,
    iconWrapperColor = 'primary.light',
    iconWrapperHeight = '2.4rem',
    label,
    labelSx = { fontWeight: '600' },
    status,
    disabled,
    marksheetRequired,
    noShadow,
    subtitle,
    sx = {},
    mrp,
    discountedPrice,
    discountInfo,
    rightAdorment
  } = props;

  const [loading, setLoading] = useState(false);

  const displayPrice = () => {
    const CURRENCY_SYMBOL = '$';
    if (mrp || discountedPrice) {
      return (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Stack>
              <Stack direction="row" spacing={0.5} justifyContent={'flex-end'}>
                <Typography sx={{ textDecoration: 'line-through', color: '#515151' }}>
                  {CURRENCY_SYMBOL}
                  {mrp}
                </Typography>
                <Typography variant="body1" sx={{ ...labelSx, fontSize: 16 }}>
                  {CURRENCY_SYMBOL}
                  {discountedPrice}
                </Typography>
              </Stack>
              <Typography sx={{ color: '#239D56', textAlign: 'right' }}>{discountInfo}</Typography>
            </Stack>
            <ChevronRightIcon sx={{ height: '1.7rem', width: 'auto', color: 'font.light' }} />
          </Stack>
        </Box>
      );
    }
    return <ChevronRightIcon sx={{ height: '1.7rem', width: 'auto', color: 'font.light' }} />;
  };

  const renderAction = () => {
    if (import.meta.env.VITE_ORG === WES_ORG) {
      if (status == TxnState.NOT_INITIALIZED || !status) {
        return displayPrice();
      }
      return <StatusTag status={status} />;
    }
    if (rightAdorment) return rightAdorment;
    if (status) return <StatusTag status={status} />;
    return <ChevronRightIcon sx={{ height: '1.7rem', width: 'auto', color: 'font.light' }} />;
  };

  return (
    <Box
      sx={{
        ...styles.wrapper,
        borderRadius: marksheetRequired ? '8px 8px 0 0' : '8px',
        boxShadow: noShadow || marksheetRequired ? 'none' : '1px 4px 10px 0px #ADADAD29',
        width: '100%',
        ...sx,
        backgroundColor: disabled
          ? '#F2F2F2'
          : isClientCareerMosaic()
            ? '#ffffff'
            : ((sx as any)?.backgroundColor ?? 'bg.white'),
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...(isClientCareerMosaic() && !disabled
          ? {
              '&:hover': {
                background: 'linear-gradient(90deg, #FFF0D8 0%, #FFFFFF 100%);',
                cursor: 'pointer'
              }
            }
          : {})
      }}
      onClick={() => {
        if (disabled) {
          return;
        }
        onClick();
      }}>
      <Box sx={{ display: 'flex', alignItems: 'center', columnGap: '1rem' }}>
        {iconComp ? (
          <Box
            sx={{
              borderRadius: '50%',
              backgroundColor: iconWrapperColor,
              display: 'grid',
              placeItems: 'center',
              height: iconWrapperHeight,
              width: iconWrapperHeight
            }}>
            {iconComp}
          </Box>
        ) : iconImgUrl ? (
          <Avatar
            onLoad={() => setLoading(false)}
            sx={{
              backgroundColor: '#eee',
              height: iconWrapperHeight,
              width: iconWrapperHeight
            }}
            src={iconImgUrl}
            alt="icon">
            {loading ? label?.[0] : null}
          </Avatar>
        ) : (
          <BackgroundLetterAvatars
            name={label}
            sx={{ height: iconWrapperHeight, width: iconWrapperHeight }}
          />
        )}
        <Box sx={{ display: 'flex', flexFlow: 'column', rowGap: '.5rem' }}>
          <Typography variant="body1" sx={{ ...labelSx }}>
            {label}
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              sx={{ fontWeight: '500', color: 'font.medium', fontStyle: 'italic' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {renderAction()}
    </Box>
  );
}

export default VerificationTile;
